import React, { useEffect, useRef, useState } from "react";
import { useHistory, withRouter } from "react-router-dom";
import { compose } from "redux";
import { useTranslation } from "react-i18next";
import { loadStripe } from "@stripe/stripe-js";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { message } from "antd";
import AuthenticationApiSerivce from "../../../../api/AuthServices";
import {
  isRegistrationLoadRetryExhaustedError,
  isRequestCancelledError,
} from "../../../../api/requestRetry";
import { resolveUserSubmitErrorMessage } from "../../../../api/submitRequestErrors";
import { useLockViewportScroll } from "../../../../hook/useLockViewportScroll";
import { hasRegistrationSignupSession } from "../../registrationAuth";
import {
  extractCardLastFour,
  getRegistrationData,
  isCardOnFileComplete,
  updateRegistrationData,
} from "../../registrationSetupStorage";
import {
  redirectIfProviderUnavailableBeforeApi,
  redirectIfProviderUnavailableFromResponse,
} from "../../registrationRdAvailability";
import { RegistrationLayout } from "../../../RegistrationLayout/RegistrationLayout";
import { RegistrationSetupNavbar } from "../../../RegistrationLayout/RegistrationSetupNavbar";
import RegistrationSetupWrapper from "../../RegistrationSetup.style";
import CancellationPolicyModal from "../CancellationPolicyModal/CancellationPolicyModal";
import enhancer, {
  cardOnFileValidationMessageFallbacks,
  extractPublishableKey,
  extractSetupIntentClientSecret,
  extractSetupIntentId,
  getStoredSetupIntentId,
  isCardOnFileFormComplete,
  isStripeApiSuccess,
} from "./RegistrationCardOnFile.validator";
import {
  REGISTRATION_SETUP_STEP_META,
  REGISTRATION_SETUP_TOTAL_STEPS,
} from "../../../../helper/constants";
import { registrationStepBack } from "../../registrationNavigation";
import {
  trackRegistrationError,
  trackRegistrationStepCompleted,
} from "../../../../helper/registrationAnalytics";

const CARD_ON_FILE_STEP = REGISTRATION_SETUP_STEP_META.CARD_ON_FILE;
const CARD_ON_FILE_INSURANCE_STEP =
  REGISTRATION_SETUP_STEP_META.CARD_ON_FILE_INSURANCE;

const DIETITIAN_SELECTION_PATH = "/register/account/dietitians";

const STRIPE_ELEMENT_STYLE = {
  base: {
    fontSize: "15px",
    lineHeight: "24px",
    color: "#2a2a2a",
    fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
    "::placeholder": {
      color: "#9aa8b8",
    },
  },
  invalid: {
    color: "#c4614a",
  },
};

const CARD_NUMBER_ELEMENT_OPTIONS = {
  showIcon: true,
  style: STRIPE_ELEMENT_STYLE,
};

const CARD_EXPIRY_ELEMENT_OPTIONS = {
  style: STRIPE_ELEMENT_STYLE,
};

const CARD_CVC_ELEMENT_OPTIONS = {
  style: STRIPE_ELEMENT_STYLE,
};

const STRIPE_BRAND_LABELS = {
  visa: "VISA",
  mastercard: "MASTERCARD",
  amex: "AMEX",
  discover: "DISCOVER",
};

const persistSetupIntentData = (setupIntentData) => {
  const clientSecret = extractSetupIntentClientSecret(setupIntentData);
  const setupIntentId = extractSetupIntentId(setupIntentData);
  updateRegistrationData({
    stripeSetupIntent: setupIntentData,
    ...(clientSecret ? { stripeSetupIntentClientSecret: clientSecret } : {}),
    ...(setupIntentId ? { stripeSetupIntentId: setupIntentId } : {}),
  });
  return { clientSecret, setupIntentId };
};

const formatPreviewCardNumber = (cardNumberState) => {
  if (!cardNumberState?.hasValue) {
    return "•••• •••• •••• ••••";
  }

  if (cardNumberState.last4) {
    return `•••• •••• •••• ${cardNumberState.last4}`;
  }

  return "•••• •••• •••• ••••";
};

const getLast4FromCardNumberEvent = (event) => event.last4 || "";

const formatExpiryFromStripeEvent = (event) => {
  const month =
    event.exp_month ?? event.expMonth ?? event.value?.expMonth ?? event.value?.exp_month;
  const year =
    event.exp_year ?? event.expYear ?? event.value?.expYear ?? event.value?.exp_year;

  if (month != null && year != null) {
    const mm = String(month).padStart(2, "0");
    const yy =
      String(year).length > 2
        ? String(year).slice(-2)
        : String(year).padStart(2, "0");
    return `${mm} / ${yy}`;
  }

  if (event.complete) {
    return null;
  }

  return "MM / YY";
};

const formatPreviewExpiry = (cardExpiryState, expiryPlaceholder) => {
  if (!cardExpiryState?.hasValue) {
    return expiryPlaceholder;
  }
  return cardExpiryState.display || expiryPlaceholder;
};

const formatExpiryFromPaymentMethodCard = (card) => {
  if (!card?.exp_month || !card?.exp_year) {
    return null;
  }
  const mm = String(card.exp_month).padStart(2, "0");
  const yy = String(card.exp_year).slice(-2);
  return `${mm} / ${yy}`;
};

const applyTokenCardPreview = (card, setCardNumberState, setCardExpiryState, setCardBrand) => {
  if (!card) {
    return;
  }

  if (card.last4) {
    setCardNumberState((prev) => ({
      ...prev,
      hasValue: true,
      complete: true,
      last4: card.last4,
    }));
  }

  const expiryDisplay = formatExpiryFromPaymentMethodCard(card);
  if (expiryDisplay) {
    setCardExpiryState((prev) => ({
      ...prev,
      hasValue: true,
      complete: true,
      display: expiryDisplay,
    }));
  }

  if (card.brand) {
    setCardBrand(card.brand);
  }
};

const getCardPreview = (
  cardholderName,
  cardBrand,
  cardNumberState,
  cardExpiryState,
  t
) => ({
  previewNumber: formatPreviewCardNumber(cardNumberState),
  previewName:
    (cardholderName || "").trim().toUpperCase() ||
    t("NUTRITION_CARD_PREVIEW_LBL_DEFAULT_NAME"),
  previewExpiry: formatPreviewExpiry(
    cardExpiryState,
    t("NUTRITION_CARD_PREVIEW_PLACEHOLDER_EXPIRY")
  ),
  previewBrand:
    STRIPE_BRAND_LABELS[cardBrand] || t("NUTRITION_CARD_PREVIEW_LBL_BRAND"),
});

const renderCardSubtitleWithPolicyLink = (t, onOpenPolicy) => {
  const subtitle = t("NUTRITION_CARD_DESC_SUBTITLE");
  const linkLabel = t("NUTRITION_CANCELLATION_LBL_TITLE");
  const matchPhrases = [linkLabel, "cancellation policy"].filter(Boolean);

  for (const phrase of matchPhrases) {
    const index = subtitle.toLowerCase().lastIndexOf(phrase.toLowerCase());
    if (index === -1) continue;

    const before = subtitle.slice(0, index);
    const matched = subtitle.slice(index, index + phrase.length);
    const after = subtitle.slice(index + phrase.length);

    return (
      <>
        {before}
        <button
          type="button"
          className="policy-link"
          onClick={onOpenPolicy}
        >
          {matched}
        </button>
        {after}
      </>
    );
  }

  return (
    <>
      {subtitle}{" "}
      <button type="button" className="policy-link" onClick={onOpenPolicy}>
        {linkLabel}
      </button>
    </>
  );
};

const RegistrationCardOnFileForm = (props) => {
  const { history, clientSecret } = props;
  const { t, i18n } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [isCancelPolicyOpen, setIsCancelPolicyOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [cardBrand, setCardBrand] = useState("");
  const [cardNumberState, setCardNumberState] = useState({
    hasValue: false,
    complete: false,
    last4: "",
  });
  const [cardExpiryState, setCardExpiryState] = useState({
    hasValue: false,
    complete: false,
    display: "MM / YY",
  });
  const [showCvcElement, setShowCvcElement] = useState(false);
  const isSubmittingRef = useRef(false);
  const cardPreviewSyncTimerRef = useRef(null);
  const {
    setFieldValue,
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    submitCount,
    handleSubmit,
    setFieldError,
    setFieldTouched,
    validateForm,
  } = props;

  useLockViewportScroll();

  useEffect(() => {
    const registrationData = getRegistrationData();
    if (!registrationData.firstName && !registrationData.email) {
      history.replace("/register/account/payment");
      return;
    }
    if (!hasRegistrationSignupSession(registrationData)) {
      history.replace("/register/account/payment");
      return;
    }
    const fullName = `${registrationData.firstName || ""} ${registrationData.lastName || ""}`.trim();
    setFieldValue("cardholderName", fullName);
    const billingZip =
      registrationData.billingZipCode ??
      registrationData.zipCode ??
      registrationData.zip ??
      "";
    if (billingZip) {
      setFieldValue("billingZipCode", billingZip);
    }
  }, [history, setFieldValue]);

  useEffect(
    () => () => {
      if (cardPreviewSyncTimerRef.current) {
        clearTimeout(cardPreviewSyncTimerRef.current);
      }
    },
    []
  );

  const scheduleCardPreviewSync = () => {
    if (!stripe || !elements) {
      return;
    }

    if (elements.getElement(CardCvcElement)) {
      return;
    }

    if (cardPreviewSyncTimerRef.current) {
      clearTimeout(cardPreviewSyncTimerRef.current);
    }

    cardPreviewSyncTimerRef.current = setTimeout(async () => {
      if (elements.getElement(CardCvcElement)) {
        return;
      }

      const cardNumberElement = elements.getElement(CardNumberElement);
      if (!cardNumberElement) {
        return;
      }

      const { token, error } = await stripe.createToken(cardNumberElement);
      if (error || !token?.card) {
        return;
      }

      applyTokenCardPreview(
        token.card,
        setCardNumberState,
        setCardExpiryState,
        setCardBrand
      );
    }, 150);
  };

  const mountCvcElement = () => {
    setShowCvcElement(true);
  };

  const showError = (field) => {
    if ((errors[field] && touched[field]) || submitCount > 0) {
      const key = errors[field];
      const translated = t(key);
      const messageText =
        translated !== key
          ? translated
          : cardOnFileValidationMessageFallbacks[key] || translated;
      return <div className="text-danger">{messageText}</div>;
    }
    return null;
  };

  const handleBillingZipChange = (e) => {
    setFieldValue(
      "billingZipCode",
      e.target.value.replace(/\D/g, "").slice(0, 5),
      true
    );
  };

  const handleCardNumberChange = (event) => {
    setCardBrand(event.brand || "");
    setCardNumberState({
      hasValue: !event.empty,
      complete: event.complete,
      last4: getLast4FromCardNumberEvent(event),
    });

    if (event.complete) {
      scheduleCardPreviewSync();
    }

    setFieldValue("cardNumberElement", event.complete ? "valid" : null, false);
    if (event.error) {
      setFieldError("cardNumberElement", event.error.message);
    } else {
      setFieldError("cardNumberElement", undefined);
    }
  };

  const handleCardExpiryChange = (event) => {
    if (event.empty) {
      setCardExpiryState({
        hasValue: false,
        complete: false,
        display: "MM / YY",
      });
    } else {
      const formattedExpiry = formatExpiryFromStripeEvent(event);
      setCardExpiryState((prev) => ({
        hasValue: true,
        complete: event.complete,
        display:
          formattedExpiry ||
          (event.complete ? prev.display : "•• / ••"),
      }));

      if (event.complete) {
        scheduleCardPreviewSync();
      }
    }

    setFieldValue("cardExpiryElement", event.complete ? "valid" : null, false);
    if (event.error) {
      setFieldError("cardExpiryElement", event.error.message);
    } else {
      setFieldError("cardExpiryElement", undefined);
    }
  };

  const handleCardCvcChange = (event) => {
    setFieldValue("cardCvcElement", event.complete ? "valid" : null, false);
    if (event.error) {
      setFieldError("cardCvcElement", event.error.message);
    } else {
      setFieldError("cardCvcElement", undefined);
    }
  };

  const onSubmit = async () => {
    if (isSubmittingRef.current || isSaving) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSaving(true);

    let didSucceed = false;

    try {
      const formErrors = await validateForm();
      if (Object.keys(formErrors).length > 0) {
        handleSubmit();
        return;
      }

      if (!stripe || !elements) {
        message.error(t("NUTRITION_CARD_ERR_FORM_LOADING"));
        return;
      }

      if (!showCvcElement) {
        mountCvcElement();
        message.warning(t("NUTRITION_CARD_ERR_ENTER_CVV"));
        return;
      }

      if (!clientSecret) {
        message.error(t("NUTRITION_COMMON_ERR_TRY_AGAIN"));
        return;
      }

      const cardNumberElement = elements.getElement(CardNumberElement);
      if (!cardNumberElement) {
        message.error(t("NUTRITION_CARD_ERR_FORM_LOADING"));
        return;
      }

      const registrationData = getRegistrationData();
      const existingSetupIntentId = getStoredSetupIntentId(registrationData);
      if (
        existingSetupIntentId &&
        registrationData.cardStoredSetupIntentId === existingSetupIntentId
      ) {
        if (!isCardOnFileComplete(registrationData)) {
          updateRegistrationData({
            cardOnFile: {
              ...registrationData.cardOnFile,
              cardholderName: values.cardholderName.trim(),
              saved: true,
            },
          });
        }
        didSucceed = true;
        trackRegistrationStepCompleted("card_on_file", 4);
        history.replace(DIETITIAN_SELECTION_PATH);
        return;
      }

      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardNumberElement,
          billing_details: {
            name: values.cardholderName.trim(),
            address: {
              postal_code: values.billingZipCode,
            },
          },
        },
      });

      if (result.error) {
        setFieldError("cardNumberElement", result.error.message);
        message.error(result.error.message);
        return;
      }

      if (result.setupIntent?.status !== "succeeded") {
        message.error(
          t("NUTRITION_CARD_ERR_INVALID_CARD")
        );
        return;
      }

      const setupIntentId = result.setupIntent.id;
      if (!setupIntentId) {
        message.error(t("NUTRITION_COMMON_ERR_TRY_AGAIN"));
        return;
      }

      const storeCardRes = await AuthenticationApiSerivce.storeRegistrationCard(
        {
          setup_intent_id: setupIntentId,
          is_default_payment: true,
        },
        i18n.language
      );

      if (!isStripeApiSuccess(storeCardRes)) {
        message.error(storeCardRes?.message || t("NUTRITION_CARD_ERR_SAVE_CARD"));
        return;
      }

      const lastFour = extractCardLastFour(result.setupIntent, storeCardRes);
      if (lastFour) {
        setCardNumberState((prev) => ({
          ...prev,
          complete: true,
          last4: lastFour,
        }));
      }

      const latestRegistrationData = getRegistrationData();
      updateRegistrationData({
        cardStoredSetupIntentId: setupIntentId,
        cardOnFile: {
          cardholderName: values.cardholderName.trim(),
          billingZipCode: values.billingZipCode,
          ...(lastFour ? { lastFour } : {}),
          saved: true,
        },
        ...(latestRegistrationData.paymentType !== "insurance"
          ? { paymentType: "selfpay" }
          : {}),
      });
      didSucceed = true;
      trackRegistrationStepCompleted("card_on_file", 4);
      history.replace(DIETITIAN_SELECTION_PATH);
    } catch (err) {
      trackRegistrationError({
        stepName: "card_on_file",
        stepNumber: 4,
        errorType: "api_failed",
      });
      message.error(
        resolveUserSubmitErrorMessage(err, {
          fallback: t("NUTRITION_COMMON_ERR_TRY_AGAIN"),
          translate: t,
        })
      );
    } finally {
      if (!didSucceed) {
        isSubmittingRef.current = false;
        setIsSaving(false);
      }
    }
  };

  const preview = getCardPreview(
    values.cardholderName,
    cardBrand,
    cardNumberState,
    cardExpiryState,
    t
  );

  const stripeReady = Boolean(stripe && elements);
  const canSaveCard =
    isCardOnFileFormComplete(values) &&
    !isSaving &&
    stripeReady &&
    Boolean(clientSecret);

  let isCardOnFileStepFour = false;
  const savedNav = localStorage.getItem("data");
  if (savedNav) {
    try {
      const parsed = JSON.parse(savedNav);
      isCardOnFileStepFour =
        parsed.paymentType === "insurance" ||
        parsed.paymentType === "selfpay" ||
        Boolean(parsed.coverageResult);
    } catch {
      isCardOnFileStepFour = false;
    }
  }

  const cardStep = isCardOnFileStepFour
    ? CARD_ON_FILE_INSURANCE_STEP
    : CARD_ON_FILE_STEP;

  return (
    <RegistrationSetupWrapper>
      <RegistrationLayout
        containScroll
        header={
          <RegistrationSetupNavbar
            stepMeta={cardStep}
            totalSteps={REGISTRATION_SETUP_TOTAL_STEPS}
            onBack={() => registrationStepBack(history)}
          />
        }
      >
        <div className="scroll-body">
          <div className="content-card">
            <form
              className="content-card-scroll"
              onSubmit={(e) => e.preventDefault()}
              noValidate
            >
              <div className="section-eyebrow">{t("NUTRITION_CARD_LBL_EYEBROW")}</div>
              <h2 className="section-title">{t("NUTRITION_CARD_LBL_TITLE")}</h2>
              <p className="section-sub">
                {renderCardSubtitleWithPolicyLink(t, () =>
                  setIsCancelPolicyOpen(true)
                )}
              </p>

              <div className="cc-visual">
                <div className="cc-chip" />
                <div className="cc-number">{preview.previewNumber}</div>
                <div className="cc-meta">
                  <div>
                    <div className="cc-meta-label">
                      {t("NUTRITION_CARD_PREVIEW_LBL_CARDHOLDER")}
                    </div>
                    <div className="cc-meta-val">{preview.previewName}</div>
                  </div>
                  <div>
                    <div className="cc-meta-label">
                      {t("NUTRITION_CARD_PREVIEW_LBL_EXPIRES")}
                    </div>
                    <div className="cc-meta-val">{preview.previewExpiry}</div>
                  </div>
                </div>
                <div className="cc-brand">{preview.previewBrand}</div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="cardholderName">
                  {t("ADDCARD_PLACEHOLDER_CARDHOLDER_NAME")}{" "}
                  <span className="required-mark">*</span>
                </label>
                <input
                  id="cardholderName"
                  name="cardholderName"
                  type="text"
                  className="form-input"
                  placeholder={t("NUTRITION_CARD_PLACEHOLDER_CARDHOLDER")}
                  value={values.cardholderName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  autoComplete="cc-name"
                />
                {showError("cardholderName")}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="cardNumberElement">
                  {t("ADDCARD_PLACEHOLDER_CARD_NUMBER")}{" "}
                  <span className="required-mark">*</span>
                </label>
                <div className="stripe-card-element-wrap">
                  {stripeReady ? (
                    <CardNumberElement
                      id="cardNumberElement"
                      options={CARD_NUMBER_ELEMENT_OPTIONS}
                      onChange={handleCardNumberChange}
                      onBlur={() => {
                        setFieldTouched("cardNumberElement", true);
                        scheduleCardPreviewSync();
                      }}
                    />
                  ) : (
                    <div className="stripe-card-element-loading">
                      {t("NUTRITION_CARD_LBL_LOADING_SECURE_FIELD")}
                    </div>
                  )}
                </div>
                {showError("cardNumberElement")}
              </div>

              <div className="form-row">
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" htmlFor="cardExpiryElement">
                    {t("NUTRITION_CARD_LBL_EXPIRY")}{" "}
                    <span className="required-mark">*</span>
                  </label>
                  <div className="stripe-card-element-wrap">
                    {stripeReady ? (
                      <CardExpiryElement
                        id="cardExpiryElement"
                        options={CARD_EXPIRY_ELEMENT_OPTIONS}
                        onChange={handleCardExpiryChange}
                        onBlur={() => {
                          setFieldTouched("cardExpiryElement", true);
                          scheduleCardPreviewSync();
                        }}
                      />
                    ) : (
                      <div className="stripe-card-element-loading">
                        {t("NUTRITION_COMMON_LBL_LOADING")}
                      </div>
                    )}
                  </div>
                  {showError("cardExpiryElement")}
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" htmlFor="cardCvcElement">
                    {t("ADDCARD_PLACEHOLDER_CVV")}{" "}
                    <span className="required-mark">*</span>
                  </label>
                  <div
                    className="stripe-card-element-wrap"
                    onClick={mountCvcElement}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        mountCvcElement();
                      }
                    }}
                    role="presentation"
                  >
                    {stripeReady && showCvcElement ? (
                      <CardCvcElement
                        id="cardCvcElement"
                        options={CARD_CVC_ELEMENT_OPTIONS}
                        onChange={handleCardCvcChange}
                        onBlur={() => setFieldTouched("cardCvcElement", true)}
                      />
                    ) : (
                      <div
                        id="cardCvcElement"
                        className="stripe-card-element-loading stripe-card-element-placeholder"
                        role="button"
                        tabIndex={0}
                        aria-label={t("NUTRITION_CARD_ARIA_ENTER_CVV")}
                      >
                        •••
                      </div>
                    )}
                  </div>
                  {showError("cardCvcElement")}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="billingZipCode">
                  {t("NUTRITION_CARD_LBL_ZIP")}{" "}
                  <span className="required-mark">*</span>
                </label>
                <input
                  id="billingZipCode"
                  name="billingZipCode"
                  type="text"
                  className="form-input"
                  placeholder={t("NUTRITION_CARD_PLACEHOLDER_ZIP")}
                  value={values.billingZipCode}
                  onChange={handleBillingZipChange}
                  onBlur={handleBlur}
                  inputMode="numeric"
                  maxLength={5}
                  autoComplete="postal-code"
                />
                {showError("billingZipCode")}
              </div>

              <div className="notice-box">
                <div className="notice-icon">⚕️</div>
                <div className="notice-text">
                  {t("NUTRITION_CARD_DESC_NOTICE_BILLING_LEAD + …_REST")}
                </div>
              </div>

              <div className="notice-box nutrition">
                <div className="notice-icon">🛡️</div>
                <div className="notice-text">
                  {t("NUTRITION_CARD_DESC_NOTICE_STRIPE")}
                </div>
              </div>

              <button
                type="button"
                className="btn-primary"
                disabled={!canSaveCard}
                onClick={onSubmit}
              >
                {t("NUTRITION_CARD_BTN_SAVE")}
              </button>
            </form>
          </div>
        </div>
      </RegistrationLayout>
      <CancellationPolicyModal
        isOpen={isCancelPolicyOpen}
        onClose={() => setIsCancelPolicyOpen(false)}
        language={i18n.language}
      />
    </RegistrationSetupWrapper>
  );
};

const RegistrationCardOnFileFormEnhanced = compose(
  withRouter,
  enhancer
)(RegistrationCardOnFileForm);

const RegistrationCardOnFile = () => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [isStripeConfigLoading, setIsStripeConfigLoading] = useState(true);

  useEffect(() => {
    const registrationData = getRegistrationData();
    if (!hasRegistrationSignupSession(registrationData)) {
      if (registrationData.paymentType === "selfpay") {
        history.replace("/register/account/payment");
      } else if (registrationData.coverageResult) {
        history.replace("/register/account/insurance/coverage");
      } else {
        history.replace("/register/account/payment");
      }
    }
  }, [history]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const loadStripePaymentConfig = async () => {
      const registrationData = getRegistrationData();
      if (!hasRegistrationSignupSession(registrationData)) {
        setIsStripeConfigLoading(false);
        return;
      }

      if (redirectIfProviderUnavailableBeforeApi(history, registrationData)) {
        setIsStripeConfigLoading(false);
        return;
      }

      setIsStripeConfigLoading(true);

      try {
        let publishableKey =
          registrationData.stripePublishableKey ||
          extractPublishableKey(registrationData.savePaymentTypeResponse);

        if (!publishableKey) {
          const tokenRes =
            await AuthenticationApiSerivce.getRegistrationStripePublishableToken(
              i18n.language,
              { signal: controller.signal }
            );
          if (cancelled) {
            return;
          }
          if (redirectIfProviderUnavailableFromResponse(history, tokenRes)) {
            return;
          }
          if (!isStripeApiSuccess(tokenRes)) {
            return;
          }
          publishableKey = extractPublishableKey(tokenRes);
        }

        if (!publishableKey) {
          return;
        }

        setStripePromise(loadStripe(publishableKey));

        const setupIntentRes =
          await AuthenticationApiSerivce.createRegistrationSetupIntent(
            { stripe_publishable_key: publishableKey },
            i18n.language,
            { signal: controller.signal }
          );
        if (cancelled) {
          return;
        }
        if (!isStripeApiSuccess(setupIntentRes)) {
          return;
        }

        const setupIntentData = setupIntentRes?.data ?? setupIntentRes;
        updateRegistrationData({ stripePublishableKey: publishableKey });

        const persisted = persistSetupIntentData(setupIntentData);
        if (!persisted.clientSecret) {
          return;
        }

        setClientSecret(persisted.clientSecret);
      } catch (err) {
        if (
          cancelled ||
          isRequestCancelledError(err) ||
          isRegistrationLoadRetryExhaustedError(err)
        ) {
          return;
        }
      } finally {
        if (!cancelled) {
          setIsStripeConfigLoading(false);
        }
      }
    };

    loadStripePaymentConfig();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [history, i18n.language]);

  if (isStripeConfigLoading || !stripePromise || !clientSecret) {
    return (
      <RegistrationSetupWrapper>
        <RegistrationLayout containScroll>
          <div className="scroll-body">
            <div className="content-card">
              <div className="content-card-scroll">
                <p className="section-sub">
                  {t("NUTRITION_CARD_LBL_LOADING_PAYMENT_FORM")}
                </p>
              </div>
            </div>
          </div>
        </RegistrationLayout>
      </RegistrationSetupWrapper>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <RegistrationCardOnFileFormEnhanced clientSecret={clientSecret} />
    </Elements>
  );
};

export default RegistrationCardOnFile;
